export type SubmitOutcome =
  | { ok: true }
  | { ok: false; reason: "config" }
  | { ok: false; reason: "network"; detail: string }
  | { ok: false; reason: "http"; status: number; code: string | null; detail: string };

const TIMEOUT_MS = 10_000;
const DETAIL_MAX = 300;

/** The body or the error message, short enough for one console line. */
function cut(text: string): string {
  const trimmed = text.trim();
  return trimmed.length > DETAIL_MAX ? `${trimmed.slice(0, DETAIL_MAX)}...` : trimmed;
}

/** The endpoint's own error code and the raw body, when the response carries them. */
async function readError(response: Response): Promise<{ code: string | null; detail: string }> {
  let text = "";
  try {
    text = await response.text();
  } catch {
    return { code: null, detail: "" };
  }
  let code: string | null = null;
  try {
    const body = JSON.parse(text) as { code?: unknown };
    if (typeof body.code === "string") code = body.code;
  } catch {
    // Not JSON: the status and the raw body are all we have.
  }
  return { code, detail: cut(text) };
}

async function send(
  url: string,
  key: string,
  idempotencyKey: string,
  body: string,
  signal: AbortSignal,
): Promise<Response> {
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Bond-Key": key,
      "Idempotency-Key": idempotencyKey,
    },
    body,
    credentials: "omit",
    signal,
  });
}

export interface SubmitOptions {
  url: string;
  key: string;
  idempotencyKey: string;
  payload: unknown;
  retries?: number;
}

/**
 * Posts one lead to the Bond intake API. The key and the idempotency key travel in headers, so
 * every attempt preflights; a retry replays the same idempotency key, which the endpoint answers
 * with the first row's id instead of writing a second lead.
 *
 * A missing url or key is a build problem, not a visitor problem, so it answers `config` and
 * never shows up as a network failure.
 */
export async function submitLead({
  url,
  key,
  idempotencyKey,
  payload,
  retries = 1,
}: SubmitOptions): Promise<SubmitOutcome> {
  if (url === "" || key === "") return { ok: false, reason: "config" };

  const body = JSON.stringify(payload);
  let lastError = "";
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    let response: Response;
    try {
      response = await send(url, key, idempotencyKey, body, controller.signal);
    } catch (error) {
      lastError = controller.signal.aborted
        ? `no answer within ${TIMEOUT_MS} ms`
        : error instanceof Error
          ? error.message
          : String(error);
      if (attempt < retries) continue;
      return { ok: false, reason: "network", detail: cut(lastError) };
    } finally {
      clearTimeout(timer);
    }
    if (response.ok) return { ok: true };
    const { code, detail } = await readError(response);
    if (response.status >= 400 && response.status < 500) {
      return { ok: false, reason: "http", status: response.status, code, detail };
    }
    if (attempt < retries) continue;
    return { ok: false, reason: "http", status: response.status, code, detail };
  }
  return { ok: false, reason: "network", detail: cut(lastError) };
}
